// src/pages/CameraRequests/RequestDetail.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  ArrowBack as BackIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Videocam as VideocamIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  ArrowForward as NextIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TaskAlt as CompletedIcon,
  Block as ClosedIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { cameraFootageRequestsApi } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useThemeSettings } from '../../contexts/ThemeContext';

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { t } = useThemeSettings();

  const [request, setRequest] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [exportingAudit, setExportingAudit] = useState(false);

  // Site Manager Approval State
  const [siteApprovalDialog, setSiteApprovalDialog] = useState(false);
  const [legalBasis, setLegalBasis] = useState('');
  const [siteRejectionReason, setSiteRejectionReason] = useState('');

  // DPO Approval State
  const [dpoApprovalDialog, setDpoApprovalDialog] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [retentionReason, setRetentionReason] = useState('');
  const [recipients, setRecipients] = useState('');
  const [dataSubjects, setDataSubjects] = useState('');
  const [dpoRejectionReason, setDpoRejectionReason] = useState('');

  // Support Footage State
  const [footageDialog, setFootageDialog] = useState(false);
  const [footageFound, setFootageFound] = useState(true);
  const [footagePath, setFootagePath] = useState('');
  const [footageNotes, setFootageNotes] = useState('');
  const [notFoundReason, setNotFoundReason] = useState('');

  // Load request details
  useEffect(() => {
    loadRequest();
    loadTimeline();
  }, [id]);

  const loadRequest = async () => {
    try {
      setLoading(true);
      const response = await cameraFootageRequestsApi.getById(id);

      if (response.data.success) {
        setRequest(response.data.data);
        //console.log('Loaded request:', response.data.data);
      }
    } catch (error) {
      console.error('Error loading request:', error);
      enqueueSnackbar(t ? t('CameraRequests.error_load_detail') : 'Failed to load request details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadTimeline = async () => {
    try {
      const response = await cameraFootageRequestsApi.getTimeline(id);

      if (response.data.success) {
        setTimeline(response.data.data);
      }
    } catch (error) {
      console.error('Error loading timeline:', error);
    }
  };

  // Site Manager Actions
  const handleSiteApprove = async () => {
    if (!legalBasis) {
      enqueueSnackbar(t ? t('CameraRequests.legal_basis_required') : 'Legal basis is required', { variant: 'warning' });
      return;
    }

    try {
      setActionLoading(true);
      await cameraFootageRequestsApi.siteApprove(id, { legal_basis: legalBasis });
      enqueueSnackbar(t ? t('CameraRequests.approved_successfully') : 'Request approved successfully', { variant: 'success' });
      setSiteApprovalDialog(false);
      loadRequest();
      loadTimeline();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || (t ? t('CameraRequests.error_approve') : 'Failed to approve request'), { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSiteReject = async () => {
    if (!siteRejectionReason) {
      enqueueSnackbar(t ? t('CameraRequests.rejection_reason_required') : 'Rejection reason is required', { variant: 'warning' });
      return;
    }

    try {
      setActionLoading(true);
      await cameraFootageRequestsApi.siteReject(id, { rejection_reason: siteRejectionReason });
      enqueueSnackbar(t ? t('CameraRequests.rejected') : 'Request rejected', { variant: 'info' });
      setSiteApprovalDialog(false);
      loadRequest();
      loadTimeline();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || (t ? t('CameraRequests.error_reject') : 'Failed to reject request'), { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // DPO Actions
  const handleDpoApprove = async () => {
    if (!retentionDays || !recipients) {
      enqueueSnackbar(t ? t('CameraRequests.retention_recipients_required') : 'Retention period and recipients are required', { variant: 'warning' });
      return;
    }

    try {
      setActionLoading(true);
      await cameraFootageRequestsApi.dpoApprove(id, {
        retention_period_days: retentionDays,
        retention_period_reason: retentionReason,
        recipients: recipients.split(',').map(r => r.trim()),
        data_subjects_categories: dataSubjects
      });
      enqueueSnackbar(t ? t('CameraRequests.dpo_approved') : 'Request approved by DPO', { variant: 'success' });
      setDpoApprovalDialog(false);
      loadRequest();
      loadTimeline();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || (t ? t('CameraRequests.error_approve') : 'Failed to approve request'), { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDpoReject = async () => {
    if (!dpoRejectionReason) {
      enqueueSnackbar(t ? t('CameraRequests.rejection_reason_required') : 'Rejection reason is required', { variant: 'warning' });
      return;
    }

    try {
      setActionLoading(true);
      await cameraFootageRequestsApi.dpoReject(id, { rejection_reason: dpoRejectionReason });
      enqueueSnackbar(t ? t('CameraRequests.dpo_rejected') : 'Request rejected by DPO', { variant: 'info' });
      setDpoApprovalDialog(false);
      loadRequest();
      loadTimeline();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || (t ? t('CameraRequests.error_reject') : 'Failed to reject request'), { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Support Actions
  const handleAddFootage = async () => {
    if (footageFound && !footagePath) {
      enqueueSnackbar(t ? t('CameraRequests.file_path_required') : 'File path is required when footage is found', { variant: 'warning' });
      return;
    }

    if (!footageFound && !notFoundReason) {
      enqueueSnackbar(t ? t('CameraRequests.not_found_reason_required') : 'Reason is required when footage is not found', { variant: 'warning' });
      return;
    }

    try {
      setActionLoading(true);
      await cameraFootageRequestsApi.addFootage(id, {
        footage_found: footageFound,
        footage_file_path: footageFound ? footagePath : null,
        footage_notes: footageNotes,
        footage_not_found_reason: !footageFound ? notFoundReason : null
      });
      enqueueSnackbar(
        footageFound ? (t ? t('CameraRequests.footage_added') : 'Footage added successfully') : (t ? t('CameraRequests.footage_not_found_marked') : 'No footage marked'),
        { variant: 'success' }
      );
      setFootageDialog(false);
      loadRequest();
      loadTimeline();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || (t ? t('CameraRequests.error_update_footage') : 'Failed to update footage'), { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportAuditReport = async () => {
    try {
      setExportingAudit(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

      const response = await fetch(
        `${API_URL}/api/camera-footage-requests/${id}/export/audit-report`,
        {
          headers: { Authorization: `Bearer ${token}` },
          method: 'GET'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export audit report');
      }

      // Create a blob URL and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or create default
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `camera-request-${id}-audit-report.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      enqueueSnackbar(
        t ? t('CameraRequests.audit_report_success') : 'Processing audit report exported successfully',
        { variant: 'success' }
      );
    } catch (error) {
      console.error('Error exporting audit report:', error);
      enqueueSnackbar(
        error.response?.data?.error || (t ? t('CameraRequests.audit_report_error') : 'Failed to export processing audit report'),
        { variant: 'error' }
      );
    } finally {
      setExportingAudit(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!request) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{t ? t('CameraRequests.request_not_found') : 'Request not found'}</Alert>
      </Container>
    );
  }

  const canApproveSite = hasPermission('camera_requests.approve_site') &&
    request.status === 'pending_site_approval' &&
    request.site_manager_email === user?.email;

  const canApproveDpo = hasPermission('camera_requests.approve_dpo') &&
    request.status === 'pending_dpo_approval';

  const canAddFootage = hasPermission('camera_requests.retrieve_footage') &&
    request.status === 'approved_pending_retrieval';

  // Determine next action
  const getNextAction = () => {
    switch (request.status) {
      case 'pending_site_approval':
        return {
          role: t ? t('CameraRequests.role_site_manager') : 'Site Manager',
          person: request.site_manager_email,
          action: t ? t('CameraRequests.action_review_approve') : 'Review and approve/reject the request',
          isCurrentUser: request.site_manager_email === user?.email,
          color: 'warning',
          needsAction: true,
          icon: NextIcon
        };
      case 'pending_dpo_approval':
        return {
          role: t ? t('CameraRequests.role_dpo') : 'Data Protection Officer (DPO)',
          person: t ? t('CameraRequests.dpo_team') : 'DPO Team',
          action: t ? t('CameraRequests.action_dpo_review') : 'Review GDPR compliance and set retention period',
          isCurrentUser: hasPermission('camera_requests.approve_dpo'),
          color: 'info',
          needsAction: true,
          icon: NextIcon
        };
      case 'approved_pending_retrieval':
        return {
          role: t ? t('CameraRequests.role_support') : 'Support Team',
          person: t ? t('CameraRequests.technical_support') : 'Technical Support',
          action: t ? t('CameraRequests.action_retrieve_footage') : 'Retrieve camera footage and upload',
          isCurrentUser: hasPermission('camera_requests.retrieve_footage'),
          color: 'primary',
          needsAction: true,
          icon: NextIcon
        };
      case 'completed':
        return {
          role: t ? t('CameraRequests.status_completed') : 'Completed',
          person: '',
          action: t ? t('CameraRequests.request_completed') : 'Request has been successfully completed. Footage has been retrieved and provided to the requester.',
          isCurrentUser: false,
          color: 'success',
          needsAction: false,
          icon: CompletedIcon
        };
      case 'rejected_by_site_manager':
      case 'rejected_by_dpo':
      case 'closed_footage_not_found':
        return {
          role: t ? t('CameraRequests.status_closed') : 'Closed',
          person: '',
          action: t ? t('CameraRequests.request_closed') : 'Request has been closed. No further action will be taken.',
          isCurrentUser: false,
          color: 'error',
          needsAction: false,
          icon: ClosedIcon
        };
      default:
        return null;
    }
  };

  const nextAction = getNextAction();

  //console.log('Id = ', request.id);
  //console.log('Created at = ', request.created_at);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/camera-requests')}>
          {t ? t('CameraRequests.back_to_requests') : 'Back to Requests'}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<PdfIcon />}
          onClick={handleExportAuditReport}
          disabled={exportingAudit}
        >
          {exportingAudit
            ? (t ? t('CameraRequests.exporting_audit') : 'Exporting audit report...')
            : (t ? t('CameraRequests.export_audit_report') : 'Export Processing Audit Report')
          }
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <VideocamIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <div>
              <Typography variant="h4">
                {t ? t('CameraRequests.request_title', { id: request.id }) : `Camera Footage Request #${request.id}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t
                  ? t('CameraRequests.created_at', {
                    date: format(new Date(request.created_at), 'PPP HH:mm')
                  })
                  : `Created on ${format(new Date(request.created_at), 'PPP HH:mm')}`
                }
              </Typography>
            </div>
          </Box>

          <Chip
            label={request.status.replace(/_/g, ' ').toUpperCase()}
            color={
              request.status.includes('rejected') || request.status.includes('closed') ? 'error' :
                request.status.includes('completed') ? 'success' :
                  request.status.includes('pending') ? 'warning' : 'primary'
            }
            size="large"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Request Details */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">{t ? t('CameraRequests.location') : 'Location'}</Typography>
            <Typography variant="body1" gutterBottom>
              {request.location?.friendlyName}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">{t ? t('CameraRequests.requested_by') : 'Requested By'}</Typography>
            <Typography variant="body1" gutterBottom>
              {request.createdByUser?.username} ({request.createdByUser?.email})
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">{t ? t('CameraRequests.location_description') : 'Location Description'}</Typography>
            <Typography variant="body1" gutterBottom>
              {request.location_description}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">{t ? t('CameraRequests.incident_datetime') : 'Incident Date & Time'}</Typography>
            <Typography variant="body1" gutterBottom>
              {format(new Date(request.incident_datetime), 'PPP HH:mm')}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">{t ? t('CameraRequests.incident_description') : 'Incident Description'}</Typography>
            <Typography variant="body1" gutterBottom>
              {request.incident_description}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">{t ? t('CameraRequests.reason_for_request') : 'Reason for Request'}</Typography>
            <Typography variant="body1" gutterBottom>
              {request.reason_for_request}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Next Action Required / Request Status */}
      {nextAction && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            bgcolor: nextAction.needsAction
              ? (nextAction.isCurrentUser ? `${nextAction.color}.50` : 'background.paper')
              : `${nextAction.color}.50`,
            border: nextAction.needsAction
              ? (nextAction.isCurrentUser ? 2 : 1)
              : 2,
            borderColor: nextAction.needsAction
              ? (nextAction.isCurrentUser ? `${nextAction.color}.main` : 'divider')
              : `${nextAction.color}.main`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {nextAction.icon && (
              <nextAction.icon sx={{ fontSize: 40, color: `${nextAction.color}.main`, mt: 0.5 }} />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                {nextAction.needsAction
                  ? (nextAction.isCurrentUser
                    ? (t ? t('CameraRequests.your_action_required') : 'Your Action Required')
                    : (t ? t('CameraRequests.next_action_required') : 'Next Action Required'))
                  : (t ? t('CameraRequests.request_status') : 'Request Status')}
              </Typography>

              {nextAction.needsAction ? (
                <>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t ? t('CameraRequests.assigned_to') : 'Assigned To'}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {nextAction.role}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {nextAction.person}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t ? t('CameraRequests.required_action') : 'Required Action'}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {nextAction.action}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  {nextAction.isCurrentUser && (
                    <Alert severity={nextAction.color} sx={{ mt: 2 }}>
                      <strong>{t ? t('CameraRequests.assigned_to_you') : 'You are assigned to this task.'}</strong> {t ? t('CameraRequests.review_and_action') : 'Please review the request details below and take the appropriate action.'}
                    </Alert>
                  )}
                </>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <Alert severity={nextAction.color} sx={{ fontSize: '1rem' }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                      {nextAction.role}
                    </Typography>
                    <Typography variant="body1">
                      {nextAction.action}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                      {t ? t('CameraRequests.no_further_action') : 'No further action required.'}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Site Manager Approval Section */}
      {canApproveSite && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'warning.50' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>{t ? t('CameraRequests.action_required_label') : 'Action Required:'}</strong> {t ? t('CameraRequests.site_manager_action_needed') : 'As the site manager, you need to review and approve or reject this request.'}
          </Alert>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
              onClick={() => setSiteApprovalDialog(true)}
            >
              {t ? t('CameraRequests.approve_request') : 'Approve Request'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<RejectIcon />}
              onClick={() => {
                setSiteApprovalDialog(true);
                setLegalBasis(''); // Clear to indicate rejection
              }}
            >
              {t ? t('CameraRequests.reject_request') : 'Reject Request'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* DPO Approval Section */}
      {canApproveDpo && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{t ? t('CameraRequests.dpo_review_required') : 'DPO Review Required:'}</strong> {t ? t('CameraRequests.dpo_review_instructions') : 'Please review the request and provide GDPR compliance details.'}
          </Alert>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
              onClick={() => setDpoApprovalDialog(true)}
            >
              {t ? t('CameraRequests.approve_as_dpo') : 'Approve as DPO'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<RejectIcon />}
              onClick={() => setDpoApprovalDialog(true)}
            >
              {t ? t('CameraRequests.reject_as_dpo') : 'Reject as DPO'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Support Footage Section */}
      {canAddFootage && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>{t ? t('CameraRequests.footage_retrieval') : 'Footage Retrieval:'}</strong> {t ? t('CameraRequests.footage_retrieval_instructions') : 'This request has been approved. Please retrieve and add the footage.'}
          </Alert>

          <Button
            variant="contained"
            startIcon={<VideocamIcon />}
            onClick={() => setFootageDialog(true)}
          >
            {t ? t('CameraRequests.add_footage_button') : 'Add Footage / Mark as Not Found'}
          </Button>
        </Paper>
      )}

      {/* Approval History */}
      {(request.site_manager_approved !== null || request.dpo_approved !== null || request.footage_found !== null) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>{t ? t('CameraRequests.approval_history') : 'Approval History'}</Typography>

          <Grid container spacing={2}>
            {request.site_manager_approved !== null && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2">{t ? t('CameraRequests.site_manager_decision') : 'Site Manager Decision'}</Typography>
                    <Chip
                      label={request.site_manager_approved ? (t ? t('Common.approved') : 'Approved') : (t ? t('Common.rejected') : 'Rejected')}
                      color={request.site_manager_approved ? 'success' : 'error'}
                      size="small"
                      sx={{ mt: 1, mb: 1 }}
                    />
                    {request.legal_basis && (
                      <Typography variant="body2">
                        {t ? t('CameraRequests.legal_basis') : 'Legal Basis'}: {request.legal_basis}
                      </Typography>
                    )}
                    {request.site_manager_rejection_reason && (
                      <Typography variant="body2" color="error">
                        {t ? t('Common.reason') : 'Reason'}: {request.site_manager_rejection_reason}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(request.site_manager_response_date), 'PPP HH:mm')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {request.dpo_approved !== null && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2">{t ? t('CameraRequests.dpo_decision') : 'DPO Decision'}</Typography>
                    <Chip
                      label={request.dpo_approved ? (t ? t('Common.approved') : 'Approved') : (t ? t('Common.rejected') : 'Rejected')}
                      color={request.dpo_approved ? 'success' : 'error'}
                      size="small"
                      sx={{ mt: 1, mb: 1 }}
                    />
                    {request.retention_period_days && (
                      <Typography variant="body2">
                        {t ? t('CameraRequests.retention') : 'Retention'}: {request.retention_period_days} {t ? t('Common.days') : 'days'}
                      </Typography>
                    )}
                    {request.dpo_rejection_reason && (
                      <Typography variant="body2" color="error">
                        {t ? t('Common.reason') : 'Reason'}: {request.dpo_rejection_reason}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(request.dpo_response_date), 'PPP HH:mm')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {request.footage_found !== null && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2">{t ? t('CameraRequests.footage_retrieval') : 'Footage Retrieval'}</Typography>
                    <Chip
                      label={request.footage_found ? (t ? t('CameraRequests.footage_found') : 'Footage Found') : (t ? t('CameraRequests.not_found') : 'Not Found')}
                      color={request.footage_found ? 'success' : 'warning'}
                      size="small"
                      sx={{ mt: 1, mb: 1 }}
                    />
                    {request.footage_file_path && (
                      <>
                        <Typography variant="body2">
                          {t ? t('CameraRequests.path') : 'Path'}: {request.footage_file_path}
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<DownloadIcon />}
                          onClick={() => {
                            // Check if it's a URL or file path
                            if (request.footage_file_path.startsWith('http://') || request.footage_file_path.startsWith('https://')) {
                              window.open(request.footage_file_path, '_blank');
                            } else {
                              // For file paths, create a download link
                              const link = document.createElement('a');
                              link.href = request.footage_file_path;
                              link.download = `footage_request_${request.id}.mp4`;
                              link.click();
                            }
                          }}
                          sx={{ mt: 1 }}
                          size="small"
                        >
                          {t ? t('CameraRequests.download_footage') : 'Download Footage'}
                        </Button>
                      </>
                    )}
                    {request.footage_not_found_reason && (
                      <Typography variant="body2">
                        {t ? t('Common.reason') : 'Reason'}: {request.footage_not_found_reason}
                      </Typography>
                    )}
                    {request.footage_notes && (
                      <Typography variant="body2">
                        {t ? t('Common.notes') : 'Notes'}: {request.footage_notes}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(request.footage_retrieval_date), 'PPP HH:mm')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Timeline */}
      {timeline.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>{t ? t('CameraRequests.activity_timeline') : 'Activity Timeline'}</Typography>

          <Timeline>
            {timeline.map((log, index) => (
              <TimelineItem key={log.id}>
                <TimelineOppositeContent color="text.secondary">
                  {format(new Date(log.timestamp), 'PPP HH:mm')}
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot color="primary" />
                  {index < timeline.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Typography variant="subtitle2">
                    {log.action.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t ? t('CameraRequests.by') : 'By'}: {log.user?.username || (t ? t('Common.system') : 'System')}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Paper>
      )}

      {/* Site Manager Approval Dialog */}
      <Dialog open={siteApprovalDialog} onClose={() => setSiteApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {legalBasis !== '' ? (t ? t('CameraRequests.approve_request') : 'Approve Request') : (t ? t('CameraRequests.approve_or_reject') : 'Approve or Reject Request')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t ? t('CameraRequests.legal_basis_label') : 'Legal Basis'} *</InputLabel>
              <Select
                value={legalBasis}
                onChange={(e) => setLegalBasis(e.target.value)}
                label={`${t ? t('CameraRequests.legal_basis_label') : 'Legal Basis'} *`}
              >
                <MenuItem value="">{t ? t('CameraRequests.select_to_approve') : '-- Select to Approve --'}</MenuItem>
                <MenuItem value="consent">{t ? t('CameraRequests.legal_basis_consent') : 'Consent'}</MenuItem>
                <MenuItem value="contract">{t ? t('CameraRequests.legal_basis_contract') : 'Contract'}</MenuItem>
                <MenuItem value="legal_obligation">{t ? t('CameraRequests.legal_basis_legal_obligation') : 'Legal Obligation'}</MenuItem>
                <MenuItem value="public_interest">{t ? t('CameraRequests.legal_basis_public_interest') : 'Public Interest'}</MenuItem>
                <MenuItem value="legitimate_interest">{t ? t('CameraRequests.legal_basis_legitimate_interest') : 'Legitimate Interest'}</MenuItem>
                <MenuItem value="vital_interest">{t ? t('CameraRequests.legal_basis_vital_interest') : 'Vital Interest'}</MenuItem>
              </Select>
            </FormControl>

            {legalBasis === '' && (
              <TextField
                label={`${t ? t('CameraRequests.rejection_reason_label') : 'Rejection Reason'} *`}
                multiline
                rows={3}
                value={siteRejectionReason}
                onChange={(e) => setSiteRejectionReason(e.target.value)}
                placeholder={t ? t('CameraRequests.rejection_reason_placeholder') : 'Please provide a reason for rejecting this request...'}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSiteApprovalDialog(false)}>{t ? t('Common.cancel') : 'Cancel'}</Button>
          {legalBasis ? (
            <Button
              onClick={handleSiteApprove}
              variant="contained"
              color="success"
              disabled={actionLoading}
            >
              {t ? t('Common.approve') : 'Approve'}
            </Button>
          ) : (
            <Button
              onClick={handleSiteReject}
              variant="contained"
              color="error"
              disabled={actionLoading}
            >
              {t ? t('Common.reject') : 'Reject'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* DPO Approval Dialog */}
      <Dialog open={dpoApprovalDialog} onClose={() => setDpoApprovalDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t ? t('CameraRequests.dpo_review') : 'DPO Review'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={`${t ? t('CameraRequests.retention_period_days') : 'Retention Period (days)'} *`}
              type="number"
              value={retentionDays}
              onChange={(e) => setRetentionDays(e.target.value)}
              helperText={t ? t('CameraRequests.retention_period_helper') : 'How many days should the footage be retained?'}
            />

            <TextField
              label={t ? t('CameraRequests.retention_reason') : 'Retention Reason'}
              multiline
              rows={2}
              value={retentionReason}
              onChange={(e) => setRetentionReason(e.target.value)}
              placeholder={t ? t('CameraRequests.retention_reason_placeholder') : 'Justification for the retention period...'}
            />

            <TextField
              label={`${t ? t('CameraRequests.recipients') : 'Recipients'} *`}
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              helperText={t ? t('CameraRequests.recipients_helper') : 'Comma-separated list of recipients (e.g., Security Team, Insurance Company)'}
              placeholder={t ? t('CameraRequests.recipients_placeholder') : 'Security Team, Insurance Company'}
            />

            <TextField
              label={t ? t('CameraRequests.data_subjects_categories') : 'Data Subjects Categories'}
              value={dataSubjects}
              onChange={(e) => setDataSubjects(e.target.value)}
              placeholder={t ? t('CameraRequests.data_subjects_placeholder') : 'e.g., Employees, Visitors, Contractors'}
            />

            <Divider />

            <TextField
              label={t ? t('CameraRequests.or_reject_with_reason') : 'Or Reject with Reason'}
              multiline
              rows={3}
              value={dpoRejectionReason}
              onChange={(e) => setDpoRejectionReason(e.target.value)}
              placeholder={t ? t('CameraRequests.or_reject_placeholder') : 'Leave empty to approve, or provide rejection reason...'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDpoApprovalDialog(false)}>{t ? t('Common.cancel') : 'Cancel'}</Button>
          {dpoRejectionReason ? (
            <Button
              onClick={handleDpoReject}
              variant="contained"
              color="error"
              disabled={actionLoading}
            >
              {t ? t('Common.reject') : 'Reject'}
            </Button>
          ) : (
            <Button
              onClick={handleDpoApprove}
              variant="contained"
              color="success"
              disabled={actionLoading}
            >
              {t ? t('Common.approve') : 'Approve'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Footage Dialog */}
      <Dialog open={footageDialog} onClose={() => setFootageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t ? t('CameraRequests.add_footage_info') : 'Add Footage Information'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t ? t('CameraRequests.footage_status') : 'Footage Status'}</InputLabel>
              <Select
                value={footageFound}
                onChange={(e) => setFootageFound(e.target.value)}
                label={t ? t('CameraRequests.footage_status') : 'Footage Status'}
              >
                <MenuItem value={true}>{t ? t('CameraRequests.footage_found') : 'Footage Found'}</MenuItem>
                <MenuItem value={false}>{t ? t('CameraRequests.footage_not_found') : 'Footage Not Found'}</MenuItem>
              </Select>
            </FormControl>

            {footageFound ? (
              <>
                <TextField
                  label={`${t ? t('CameraRequests.file_path_url') : 'File Path / URL'} *`}
                  value={footagePath}
                  onChange={(e) => setFootagePath(e.target.value)}
                  placeholder={t ? t('CameraRequests.file_path_placeholder') : '\\server\footage\...'}
                  helperText={t ? t('CameraRequests.file_path_helper') : 'Enter the secure file server path or URL'}
                />

                <TextField
                  label={t ? t('Common.notes') : 'Notes'}
                  multiline
                  rows={3}
                  value={footageNotes}
                  onChange={(e) => setFootageNotes(e.target.value)}
                  placeholder={t ? t('CameraRequests.footage_notes_placeholder') : 'Additional notes about the footage...'}
                />
              </>
            ) : (
              <TextField
                label={`${t ? t('CameraRequests.reason_not_found') : 'Reason Not Found'} *`}
                multiline
                rows={3}
                value={notFoundReason}
                onChange={(e) => setNotFoundReason(e.target.value)}
                placeholder={t ? t('CameraRequests.reason_not_found_placeholder') : 'Explain why the footage was not found...'}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFootageDialog(false)}>{t ? t('Common.cancel') : 'Cancel'}</Button>
          <Button
            onClick={handleAddFootage}
            variant="contained"
            disabled={actionLoading}
          >
            {t ? t('Common.submit') : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RequestDetail;
